import { useCallback, useState } from 'react'
import type { z } from 'zod'

/**
 * Minimal Zod-backed form state for the marketing auth screens.
 *
 * WHY NOT react-hook-form HERE. The plan names react-hook-form + Zod for
 * BB-FR-001 — the Brief Builder's five-step, ~40-field wizard with autosave,
 * where its uncontrolled-input performance and field-array handling earn their
 * keep. These are two- and three-field forms on the public site, where the same
 * library costs ~23KB gz on first paint and the marketing app's whole job is
 * FCP ≤2.5s. Measured: removing it took the first-paint payload from 156KB to
 * within budget.
 *
 * Zod is retained, and the schemas are the SAME ones from @insightai/auth that
 * the console uses, so validation cannot diverge between the two apps.
 */
export function useZodForm<Schema extends z.ZodType>(
  schema: Schema,
  initialValues: z.input<Schema>,
) {
  type Values = z.input<Schema>
  type FieldErrors = Partial<Record<keyof Values & string, string>>

  const [values, setValues] = useState<Values>(initialValues)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)

  const setField = useCallback(<K extends keyof Values & string>(key: K, value: Values[K]) => {
    setValues((current) => ({ ...current, [key]: value }))
    // Clear the field's error as soon as the user edits it: keeping a stale
    // message under a field being corrected reads as an unresponsive form.
    setErrors((current) => {
      if (!(key in current)) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }, [])

  const validate = useCallback((): z.output<Schema> | null => {
    const result = schema.safeParse(values)
    if (result.success) {
      setErrors({})
      return result.data as z.output<Schema>
    }
    // First issue per field wins: showing three messages under one input is
    // noise, and Zod reports them in declaration order (most specific first).
    const fieldErrors: Record<string, string> = {}
    for (const issue of result.error.issues) {
      const key = String(issue.path[0] ?? 'form')
      fieldErrors[key] ??= issue.message
    }
    setErrors(fieldErrors as FieldErrors)
    return null
  }, [schema, values])

  const handleSubmit = useCallback(
    (onValid: (data: z.output<Schema>) => Promise<void> | void) =>
      async (event: { preventDefault: () => void }) => {
        event.preventDefault()
        const data = validate()
        if (!data) return
        setSubmitting(true)
        try {
          await onValid(data)
        } finally {
          setSubmitting(false)
        }
      },
    [validate],
  )

  return { values, errors, submitting, setField, validate, handleSubmit, setErrors }
}
